import type { ApolloError } from '@apollo/client'
import type { FC } from 'react'
import styled from 'styled-components'

import DocumentRequest from '@/components/DocumentRequests/DocumentRequest'
import H3 from '@/components/H3'
import LoadingSpinner from '@/components/LoadingSpinner'
import UnexpectedError from '@/components/UnexpectedError'
import type { GetDocumentRequestsData } from '@/lib/apollo/query/get-document-requests'

const DocumentRequestsWrapper = styled.div`
  display: grid;
  gap: 10px;
`

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  triggerRefetch: () => void
  documentRequestData: GetDocumentRequestsData | undefined
  error: ApolloError | undefined
  isLoadingDocumentRequests: boolean
  subjectName: string
}

const DocumentRequestList: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  triggerRefetch,
  documentRequestData,
  isLoadingDocumentRequests,
  error,
  subjectName,
}) => {
  const activeDocumentRequests =
    documentRequestData?.getDocumentRequests.filter(({ completed }) => !completed) ?? []
  const completedDocumentRequests =
    documentRequestData?.getDocumentRequests.filter(({ completed }) => completed) ?? []

  return isLoadingDocumentRequests ? (
    <LoadingSpinner size="2x" />
  ) : error ? (
    <UnexpectedError error={error} onRetry={() => triggerRefetch()} />
  ) : (
    <>
      <H3>Awaiting response</H3>
      <DocumentRequestsWrapper>
        {activeDocumentRequests.length ? (
          activeDocumentRequests.map((documentRequest, i) => (
            <DocumentRequest
              customerId={customerId}
              applicationId={applicationId}
              subjectId={subjectId}
              key={i}
              documentRequest={documentRequest}
              subjectName={subjectName}
            />
          ))
        ) : (
          <>No requests.</>
        )}
      </DocumentRequestsWrapper>
      <H3>Received documents</H3>
      <DocumentRequestsWrapper>
        {completedDocumentRequests.length ? (
          completedDocumentRequests.map((documentRequest, i) => (
            <DocumentRequest
              customerId={customerId}
              applicationId={applicationId}
              subjectId={subjectId}
              key={i}
              documentRequest={documentRequest}
              subjectName={subjectName}
            />
          ))
        ) : (
          <>No requests.</>
        )}
      </DocumentRequestsWrapper>
    </>
  )
}

export default DocumentRequestList
